import { useEffect, useState } from "react";
import { useMutation, gql, useLazyQuery } from "@apollo/client";
import { GET_STORES } from "../../../api/store-list-queries";
import IMG from "./push-ex.jpeg";
import { useLocation } from "../../../core/location-provider";
import { notify, notifyError } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import type {
  SendPushByAdminMutation,
  SendPushByAdminMutationVariables,
} from "../../../__generated__/graphql";
import {
  AdminStoreTableWithMobileCards,
  type AdminStoreListItem,
} from "../components/admin-store-table-with-mobile-cards";
import { AdminPushMessageForm } from "../components/admin-push-message-form";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const NEW_STORE_PUSH_NEARBY_PANEL_ID = "new-store-push-nearby-panel";

const STORE_SEARCH = gql(`
  query searchStores($keyword: String!, $lat: Float, $lng: Float) {
    storeSearch(keyword: $keyword, lat: $lat, lng: $lng) {
      id
      title
      img
      lat
      lng
      area2
      address
      distance
      reserveTime
      pack {
        isTomorrowReservationEnabled
      }
    }
  }
`);

const GET_AREA3S = gql(`
  query getArea3s($area2: String!) {
    getArea3s(area2: $area2)
  }
`);

const SEND_PUSH_NOTIFICATION = gql(`
  mutation sendPushByAdmin($input: Json!) {
    sendPushByAdmin(input: $input) {
      ok
      error
    }
  }
`);

interface StoreSearchItem {
  id: number;
  title: string;
  img?: string | null;
  lat?: number | null;
  lng?: number | null;
  area2?: string | null;
  address?: string | null;
}

type NearbyStoreItem = {
  id: number;
  title: string;
  img?: string;
  likes: unknown[];
};

const AdminNewStorePushAlarm = () => {
  const { hasLastLo, getLocationFromStorage } = useLocation();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchStores, { data, loading }] = useLazyQuery(STORE_SEARCH, {
    fetchPolicy: "cache-first",
  });
  const [selectedStore, setSelectedStore] = useState<StoreSearchItem | null>(
    null,
  );
  const [nearbyStores, setNearbyStores] = useState<NearbyStoreItem[]>([]);
  const [area3s, setArea3s] = useState<string[]>([]);
  const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([]);
  const [pushTitle, setPushTitle] = useState("");
  const [pushMessage, setPushMessage] = useState("");
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [totalLikes, setTotalLikes] = useState<number>(0);
  const [messageType, setMessageType] = useState<string>("");

  useScrollToDetailPanelOnSelect(selectedStore, NEW_STORE_PUSH_NEARBY_PANEL_ID);

  const [getStores, { loading: loadingNearby }] = useLazyQuery(GET_STORES);
  const [getArea3s] = useLazyQuery(GET_AREA3S);

  const [sendPushNotification, { loading: sending }] = useMutation<
    SendPushByAdminMutation,
    SendPushByAdminMutationVariables
  >(SEND_PUSH_NOTIFICATION, {
    onCompleted: (res) => {
      if (res.sendPushByAdmin.ok) {
        notify("푸시 알림이 발송되었습니다.");
        setPushTitle("");
        setPushMessage("");
        setSelectedAreas([]);
        setTotalLikes(0);
        setSelectedStoreIds([]);
        setMessageType("");
      } else {
        notifyError(res.sendPushByAdmin.error ?? "발송에 실패했습니다.");
      }
    },
    onError: () => {
      notifyError("푸시 알림 발송 중 오류가 발생했습니다.");
    },
  });

  const handleSearch = () => {
    const term = searchKeyword.trim();
    if (!term) {
      notifyError("검색어를 입력해주세요.");
      return;
    }
    searchStores({
      variables: {
        keyword: term,
        lat: hasLastLo ? getLocationFromStorage().lat : null,
        lng: hasLastLo ? getLocationFromStorage().lng : null,
      },
    });
  };

  const handleSendNotification = () => {
    if (!pushMessage || !messageType) {
      notifyError("매장, 메시지, 메시지 타입 및 지역을 선택해주세요.");
      return;
    }
    sendPushNotification({
      variables: {
        input: {
          storeIds: selectedStoreIds,
          message: pushMessage,
          title: pushTitle,
          areas: selectedAreas,
          newStoreId: selectedStore?.id,
          messageType,
        },
      },
    });
  };

  const handleSelectStoreFromPicker = (item: AdminStoreListItem) => {
    const searchResults =
      (data as { storeSearch?: StoreSearchItem[] })?.storeSearch ?? [];
    const full = searchResults.find((s) => s.id === item.id);
    if (!full) return;
    setSelectedStore(full);
    setSelectedStoreIds([]);
    getStores({
      variables: {
        lat: full.lat ?? undefined,
        lng: full.lng ?? undefined,
      },
      onCompleted: (storesData: unknown) => {
        const list =
          (storesData as { storesV2?: NearbyStoreItem[] })?.storesV2 ?? [];
        setNearbyStores(Array.isArray(list) ? list : []);
      },
    });
    getArea3s({
      variables: { area2: full.area2 ?? "" },
      onCompleted: (areaData: unknown) => {
        setArea3s((areaData as { getArea3s?: string[] })?.getArea3s ?? []);
      },
    });
  };

  useEffect(() => {
    const total = nearbyStores.reduce(
      (sum, store) =>
        sum + (selectedStoreIds.includes(store.id) ? store.likes.length : 0),
      0,
    );
    setTotalLikes(total);
  }, [selectedStoreIds, nearbyStores]);

  const searchResults =
    (data as { storeSearch?: StoreSearchItem[] })?.storeSearch ?? [];
  const storeListItems: AdminStoreListItem[] = searchResults.map((s) => ({
    id: s.id,
    title: s.title,
    img: s.img,
    address: s.address,
  }));

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const listContent =
    loading && storeListItems.length === 0 ? (
      <div className="flex justify-center py-8">
        <LoadingSpinnerInline />
      </div>
    ) : (
      <div className="space-y-6">
        <AdminStoreTableWithMobileCards
          stores={storeListItems}
          selectionMode="single"
          selectedIds={selectedStore ? [selectedStore.id] : []}
          onSelect={handleSelectStoreFromPicker}
          emptyText={
            searchKeyword.trim()
              ? "검색 결과가 없습니다."
              : "검색어를 입력하고 검색 버튼을 누르세요."
          }
        />

        {selectedStore && (
          <div
            id={NEW_STORE_PUSH_NEARBY_PANEL_ID}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">
                근처 매장
              </h3>
            </div>
            <div className="p-4">
              {loadingNearby ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinnerInline />
                </div>
              ) : (
                <AdminStoreTableWithMobileCards
                  stores={nearbyStores.map((s) => ({
                    id: s.id,
                    title: s.title,
                    img: s.img ?? undefined,
                    address: undefined,
                  }))}
                  selectionMode="multiple"
                  selectedIds={selectedStoreIds}
                  onSelect={(store) =>
                    setSelectedStoreIds(
                      selectedStoreIds.includes(store.id)
                        ? selectedStoreIds.filter((id) => id !== store.id)
                        : [...selectedStoreIds, store.id],
                    )
                  }
                  showImage={true}
                  extraHeaders={
                    <th className="px-4 py-2 font-medium text-gray-700">
                      좋아요
                    </th>
                  }
                  renderExtraCells={(store) => {
                    const full = nearbyStores.find((s) => s.id === store.id);
                    return full ? (
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        ❤️ {full.likes?.length ?? 0}
                      </td>
                    ) : null;
                  }}
                  renderExtraCard={(store) => {
                    const full = nearbyStores.find((s) => s.id === store.id);
                    return full ? (
                      <p className="text-xs text-gray-600 mt-1">
                        ❤️ {full.likes?.length ?? 0}
                      </p>
                    ) : null;
                  }}
                  emptyText="근처 매장이 없습니다."
                />
              )}
            </div>
          </div>
        )}
      </div>
    );

  const rightPanelContent = selectedStore ? (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">지역 선택</h3>
        <div className="flex flex-wrap gap-1">
          {area3s?.map((area) => (
            <button
              key={area}
              type="button"
              onClick={() =>
                setSelectedAreas(
                  selectedAreas.includes(area)
                    ? selectedAreas.filter((a) => a !== area)
                    : [...selectedAreas, area],
                )
              }
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                selectedAreas.includes(area)
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {area}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">메시지 타입</h3>
        <div className="flex flex-wrap gap-1">
          {["예약판매알림", "실시간판매알림", "신규매장알림"].map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setMessageType(type)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                messageType === type
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <AdminPushMessageForm
        title={pushTitle}
        message={pushMessage}
        onTitleChange={setPushTitle}
        onMessageChange={setPushMessage}
        titleLabel="푸시 제목 (선택)"
        messageLabel="푸시 본문"
        titlePlaceholder="푸시 알림 제목"
        messagePlaceholder="푸시 알림 본문"
        titleRequired={false}
        messageRequired
        messageRows={5}
      />

      <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
        <span className="font-medium">{totalLikes}</span>명,{" "}
        <span className="font-medium">
          {selectedAreas.join(", ") || "(없음)"}
        </span>{" "}
        지역에 발송
      </div>

      <ResponsiveCDNImage
        src={IMG}
        alt="푸시 예시"
        className="w-full rounded-lg border border-gray-200"
        sizeRatio={0.5}
      />
      <p className="text-xs text-gray-500">* 제목 미입력 시 기본 타이틀 전송</p>

      <button
        type="button"
        onClick={handleSendNotification}
        disabled={!pushMessage || !messageType || sending}
        className="w-full px-4 py-2 bg-blue-500 text-white font-medium rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
      >
        {sending ? (
          <>
            <LoadingSpinnerInline />
            발송 중...
          </>
        ) : (
          "푸시 알림 발송"
        )}
      </button>
    </div>
  ) : null;

  return (
    <div className="w-full md:p-8">
      <h1 className="text-2xl font-bold mb-2 text-gray-800">
        새로운 매장 푸시 알림
      </h1>
      <p className="text-gray-600 text-sm mb-6">
        신규 매장을 선택하고 근처 매장·지역·메시지 타입을 설정한 뒤 푸시를
        발송하세요.
      </p>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[7fr_3fr] items-start">
        {/* 왼쪽: 검색 + 결과 + 근처 매장 */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">
              1. 매장 검색
            </h2>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="매장명·주소로 검색"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm placeholder:text-gray-400 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? "검색 중..." : "검색"}
              </button>
            </div>
          </div>
          <div className="p-2 border-b border-gray-100">
            <h3 className="text-sm font-medium text-gray-700 px-2">
              2. 검색 결과에서 선택
            </h3>
          </div>
          <div className="min-h-[32rem] max-h-[calc(100vh-8rem)] overflow-y-auto">
            {listContent}
          </div>
        </div>

        {/* 오른쪽: 푸시 발송 */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-800">
              3. 푸시 발송
            </h2>
          </div>
          <div className="p-4 space-y-4">
            {rightPanelContent ?? (
              <p className="text-gray-500 text-sm">
                신규 매장을 선택하세요.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNewStorePushAlarm;
